/* ──────────────────────────────────────────────────────────────
aiWorkplaceEmailService.js · Email service for AI Workplace applications
──────────────────────────────────────────────────────────── */

import { getFunctions, httpsCallable } from "firebase/functions";

// Email template for AI Workplace application confirmation
const createApplicationConfirmationEmail = (userData) => {
  const htmlTemplate = `<!DOCTYPE html>

<html lang="en"> <head> <meta charset="utf-8"> <title>Application Received - TPC AI Workplace</title> <meta name="viewport" content="width=device-width,initial-scale=1"> </head> <body style="margin:0; padding:0; background-color:#f4f4f4; -webkit-text-size-adjust:100%; -ms-text-size-adjust:100%;"> <!-- Preheader text --> <div style="display:none; visibility:hidden; mso-hide:all; font-size:1px; line-height:1px; max-height:0; max-width:0; opacity:0; overflow:hidden;"> Your application was successful! Welcome to TPC's AI in the Workplace Program. </div> <!-- Main wrapper table --> <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; background-color:#f4f4f4;"> <tr> <td align="center" valign="top" style="padding:20px 0;">
    <!-- Email container -->
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px; max-width:600px; margin:0 auto; background-color:#ffffff;">
      
      <!-- Header with logo and text -->
      <tr>
        <td align="center" style="padding:25px 40px; background-color:#3D4F5C;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td align="left" valign="middle" style="width:80px;">
                <img src="https://firebasestorage.googleapis.com/v0/b/tech-performance-centre.firebasestorage.app/o/websiteImages%2FandUsComponents%2FheroManagementDashboard%2FaiWorkplace%2FTPCLogo.webp?alt=media&token=70a14eee-18ac-4e0d-8866-99f2339c813a" width="80" alt="TPC Logo" style="display:block; border:0; outline:none; text-decoration:none; -ms-interpolation-mode:bicubic;">
              </td>
              <td align="left" valign="middle" style="padding-left:20px;">
                <p style="margin:0; font-family:'Georgia',serif; font-size:24px; line-height:28px; color:#ffffff; font-weight:400;">
                  TPC<br>
                  <span style="font-size:22px; letter-spacing:1px;">Performance Centre</span>
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- WELCOME heading -->
      <tr>
        <td align="center" style="padding:50px 40px 20px 40px; background-color:#ffffff;">
         <p style="margin:15px 0 0 0; font-family:'Georgia',serif; font-size:18px; color:#3D4F5C; line-height:24px; letter-spacing:2px; text-transform:uppercase;">
            Your application for TPC's AI in the Workplace Masterclass was 
          </p>
          <h1 style="margin:0; font-family:'Georgia',serif; font-size:56px; line-height:60px; color:#3D4F5C; font-weight:700; letter-spacing:-1px;">
            SUCCESSFUL!
          </h1>
        </td>
      </tr>

            <!-- Orange Call-to-Action Section -->
            <tr>
              <td class="cta-section">
                <p class="cta-text">
                  We're excited to help you harness the power of AI in your workplace. Our team will contact you within 2-3 business days.
                </p>

                <!-- Contact Information -->
                <div class="contact-container">
                  <p class="contact-heading">Need assistance? Contact us</p>
                  <div class="contact-links">
                    <a href="tel:+27120513483" class="contact-link">Call: 012 051 3483</a>
                    <a href="mailto:registrations@tpc.training" class="contact-link">Email: registrations@tpc.training</a>
            </div>
            </div>
              </td>
            </tr>
            
            <!-- Footer -->
            <tr>
              <td style="padding:50px 40px; font-family:'Georgia',serif; font-size:16px; line-height:26px; color:#ffffff; text-align:center;">
                <p style="margin:0 0 25px 0; font-size:18px; line-height:28px;">
                  We look forward to helping you improve your workflows, optimise your deliverables, and create an unforgettable experience with TPC!
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>

      <!-- Dark footer -->
      <tr>
        <td align="center" style="padding:40px; background-color:#3D4F5C;">
          <p style="margin:0 0 15px 0; font-family:'Georgia',serif; font-size:14px; color:#ffffff; line-height:22px;">
            <strong>Need assistance? Contact us:</strong>
          </p>
          <p style="margin:0; font-family:'Georgia',serif; font-size:13px; color:#ffffff; line-height:20px;">
            📞 Call: <a href="tel:+27120513483" style="color:#FFD700; text-decoration:none;">012 051 3483</a><br>
            ✉️ Email: <a href="mailto:registrations@tpc.training" style="color:#FFD700; text-decoration:none;">registrations@tpc.training</a><br>
          </p>
          <p style="margin:25px 0 0 0; font-family:'Georgia',serif; font-size:12px; color:#999999; line-height:18px;">
            © 2025 Tech Performance Centre. All rights reserved.<br>
            <a href="https://tpc.training" style="color:#999999; text-decoration:none;">Visit our website</a>
          </p>
        </td>
      </tr>

    </table>
    
  </td>
</tr>
</table> 
</body> 
</html>`;

  return {
    to: userData.email,
    subject: "Welcome to TPC's AI in the Workplace Program!",
    html: htmlTemplate,
    text: `
Hi there,

Thank you for applying to our AI in the Workplace Program. Your application has been successfully received and our team will be in touch with you soon.

Our team will review your application and contact you within 2-3 business days to discuss your personalized AI training plan.

Contact us:
- Call: 012 051 3483
- Email: registrations@tpc.training
      
      Best regards,
      The TPC Team
      Tech Performance Centre
    `,
  };
};

// Function to send application confirmation email
export const sendApplicationConfirmationEmail = async (userData) => {
  try {
    const fn = getFunctions();
    const sendEmail = httpsCallable(fn, "sendEmail");

    const emailData = createApplicationConfirmationEmail(userData);

    const result = await sendEmail({
      to: emailData.to,
      subject: emailData.subject,
      html: emailData.html,
      isHTML: true,
      from: "TPC Registrations <noreply@mail.andus.co.za>",

      replyTo: "registrations@tpc.training",
    });

    console.log(
      "Application confirmation email sent successfully:",
      result.data,
    );
    return { success: true, data: result.data };
  } catch (error) {
    console.error("Error sending application confirmation email:", error);
    return { success: false, error: error.message };
  }
};

export default {
  sendApplicationConfirmationEmail,
  createApplicationConfirmationEmail,
};
